import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { Trans } from 'react-i18next';
import {
    UIButton,
    UIExternalLinkTextButton,
    UIFormTextarea,
    UIMultiSelectInput,
    UIPaginatedMultiSelectInput,
    UIPaginatedSelectOption,
    UISelectValue,
    UIText
} from '@/ui';
import { useCompanyContext } from '@/companies/providers';
import { useFetchTeamOptions } from '@/companies/queries';
import EmergencyBroadcastImage from '../assets/images/features/emergency-broadcast-white.svg?react';
import { useSendBroadcastAction } from '../actions';
import { useBroadcastTranslation } from '../hooks';
import styles from './send-emergency-broadcast.module.scss';

const MAX_MESSAGE_LENGTH = 500;
const SMS_PRICING_URL = 'https://www.safepointapp.com/sms-pricing';

export function SendEmergencyBroadcast(): ReactElement {
    const { t } = useBroadcastTranslation();
    const { company } = useCompanyContext();
    const { sendBroadcast, loading, isAllowed, reason } =
        useSendBroadcastAction();

    // SMS is a separate subscription to the main one, and a cancelled-at-period
    // -end subscription still reads as active until it lapses.
    const smsEnabled = useMemo(() => {
        return (
            !!company.smsSubscription &&
            !company.smsSubscription.cancelAtPeriodEnd &&
            ['active', 'past_due'].includes(company.smsSubscription.status)
        );
    }, [company.smsSubscription]);

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Teams
     */
    const teamsDefault: UIPaginatedSelectOption[] = useMemo(() => [], []);
    const [teams, setTeams] = useState<UIPaginatedSelectOption[]>(teamsDefault);
    const [teamsError, setTeamsError] = useState('');
    // Only the teams I may broadcast to are listed — the server narrows the
    // page to those I administer or guard, so there is nothing to grey out and
    // no role check to repeat here.
    const teamPicker = useFetchTeamOptions({
        companyId: company.id,
        roles: ['admin', 'guardian']
    });
    const checkTeams = useCallback(
        (selectedTeams: UIPaginatedSelectOption[]): string => {
            if (selectedTeams.length === 0) {
                return t(
                    'components.send_emergency_broadcast.teams_error_required'
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamsError(checkTeams(teams));
        }
    }, [teams, showRealTimeErrors, checkTeams]);

    /**
     * Broadcast Channels
     */
    const broadcastChannelsOptions: UISelectValue[] = useMemo(
        () => [
            {
                label: t(
                    'components.send_emergency_broadcast.channel_email_label'
                ),
                subLabel: t(
                    'components.send_emergency_broadcast.channel_email_description'
                ),
                value: 'email'
            },
            {
                label: t(
                    'components.send_emergency_broadcast.channel_sms_label'
                ),
                subLabel: t(
                    'components.send_emergency_broadcast.channel_sms_description'
                ),
                value: 'sms',
                isDisabled: !smsEnabled
            }
        ],
        [smsEnabled, t]
    );

    const [broadcastChannels, setBroadcastChannels] = useState<UISelectValue[]>(
        []
    );
    const [broadcastChannelsError, setBroadcastChannelsError] = useState('');
    const checkBroadcastChannels = useCallback(
        (selectedBroadcastChannels: UISelectValue[]): string => {
            if (selectedBroadcastChannels.length === 0) {
                return t(
                    'components.send_emergency_broadcast.channels_error_required'
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setBroadcastChannelsError(
                checkBroadcastChannels(broadcastChannels)
            );
        }
    }, [broadcastChannels, showRealTimeErrors, checkBroadcastChannels]);

    /**
     * Message
     */
    const [message, setMessage] = useState('');
    const [messageError, setMessageError] = useState('');
    const currentMessageLengthText = useMemo(() => {
        const sanitisedMessage = message.trim();
        if (sanitisedMessage.length === 0) {
            return '';
        }

        return `${sanitisedMessage.length}/${MAX_MESSAGE_LENGTH}`;
    }, [message]);
    const checkMessage = useCallback(
        (msg: string): string => {
            const sanitisedMessage = msg.trim();
            if (sanitisedMessage.length === 0) {
                return t(
                    'components.send_emergency_broadcast.message_error_required'
                );
            } else if (sanitisedMessage.length > MAX_MESSAGE_LENGTH) {
                return t(
                    'components.send_emergency_broadcast.message_error_too_long',
                    {
                        length: sanitisedMessage.length,
                        maxLength: MAX_MESSAGE_LENGTH
                    }
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setMessageError(checkMessage(message));
        }
    }, [message, showRealTimeErrors, checkMessage]);

    function resetForm() {
        setTeams(teamsDefault);
        setBroadcastChannels([]);
        setMessage('');
        setTeamsError('');
        setBroadcastChannelsError('');
        setMessageError('');
        setShowRealTimeErrors(false);
    }

    function submitForm() {
        setShowRealTimeErrors(true);

        if (
            checkTeams(teams) ||
            checkBroadcastChannels(broadcastChannels) ||
            checkMessage(message)
        ) {
            return;
        }

        setShowRealTimeErrors(false);

        sendBroadcast({
            teamIds: teams.map((team) => team.value),
            deliveryMethods: broadcastChannels.map(
                (channel) => channel.value
            ) as ('app' | 'email' | 'sms')[],
            message: message.trim()
        })
            .then(() => {
                resetForm();
            })
            .catch(() => {}); // Error handled in the action
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        submitForm();
    };

    return (
        <div className={styles.container}>
            <div className={styles.leftContainer}>
                <UIText variant="large" medium>
                    {t('components.send_emergency_broadcast.title')}
                </UIText>

                <form
                    onSubmit={handleSubmit}
                    noValidate
                    className={styles.formContainer}
                >
                    <UIPaginatedMultiSelectInput
                        label={t(
                            'components.send_emergency_broadcast.teams_label'
                        )}
                        placeholder={t(
                            'components.send_emergency_broadcast.teams_placeholder'
                        )}
                        searchPlaceholder={t(
                            'components.send_emergency_broadcast.teams_search_placeholder'
                        )}
                        emptyText={t(
                            'components.send_emergency_broadcast.teams_empty'
                        )}
                        errorStateText={t(
                            'components.send_emergency_broadcast.teams_load_error'
                        )}
                        required={true}
                        options={teamPicker.options}
                        value={teams}
                        onChange={setTeams}
                        search={teamPicker.search}
                        onSearchChange={teamPicker.setSearch}
                        isLoading={teamPicker.status === 'pending'}
                        isError={teamPicker.status === 'error'}
                        isFetchingNextPage={teamPicker.isFetchingNextPage}
                        hasNextPage={teamPicker.hasNextPage}
                        onLoadMore={teamPicker.fetchNextPage}
                        onRetry={teamPicker.refetch}
                        errorText={teamsError}
                    />
                    <UIMultiSelectInput
                        label={t(
                            'components.send_emergency_broadcast.channels_label'
                        )}
                        placeholder={t(
                            'components.send_emergency_broadcast.channels_placeholder'
                        )}
                        required={true}
                        options={broadcastChannelsOptions}
                        value={broadcastChannels}
                        onChange={setBroadcastChannels}
                        errorText={broadcastChannelsError}
                    />
                    <UIFormTextarea
                        label={t(
                            'components.send_emergency_broadcast.message_label'
                        )}
                        placeholder={t(
                            'components.send_emergency_broadcast.message_placeholder'
                        )}
                        description={currentMessageLengthText}
                        value={message}
                        onChange={setMessage}
                        errorText={messageError}
                        required={true}
                        onShortcutSubmit={submitForm}
                    />

                    <div>
                        <UIButton
                            variant="primary"
                            loading={loading}
                            disabled={!isAllowed}
                            disabledTooltip={reason}
                        >
                            {t(
                                'components.send_emergency_broadcast.send_button'
                            )}
                        </UIButton>
                    </div>
                </form>
            </div>
            <div className={styles.rightContainer}>
                <div className={styles.imageContainer}>
                    <EmergencyBroadcastImage
                        width={100}
                        height={100}
                        className={styles.image}
                    />
                </div>
                <UIText variant="semiLarge" medium>
                    {t('components.send_emergency_broadcast.info_title')}
                </UIText>
                <UIText
                    type="default2"
                    style={{
                        lineHeight: '1.4rem'
                    }}
                >
                    <Trans
                        ns="broadcast"
                        i18nKey={
                            'components.send_emergency_broadcast.info_message'
                        }
                        components={{
                            b: <UIText medium type="default" />,
                            a: (
                                <UIExternalLinkTextButton
                                    variant="primary"
                                    href={SMS_PRICING_URL}
                                />
                            )
                        }}
                    />
                </UIText>
            </div>
        </div>
    );
}
